import { cloneDeep } from 'lodash';
import { RubigramStore } from './RubigramStore';
import { RubigramData, STATS_DIALOG_DELAY } from '../../../utils/types';
import { savePuzzleProgress } from '../../../utils/persistenceUtils';
import { PuzzleNames } from '../../../utils/types';

export const initRubigram = (rubigramData: RubigramData) => {
  RubigramStore.update(s => {
    s.puzzleId = rubigramData.puzzleId;
    // Numbered clockwise 0123
    s.subgrids[0].content = cloneDeep(rubigramData.scrambledGrid[0]);
    s.subgrids[1].content = cloneDeep(rubigramData.scrambledGrid[1]);
    s.subgrids[2].content = cloneDeep(rubigramData.scrambledGrid[2]);
    s.subgrids[3].content = cloneDeep(rubigramData.scrambledGrid[3]);

    s.subgrids[0].displayContent = cloneDeep(s.subgrids[0].content);
    s.subgrids[1].displayContent = cloneDeep(s.subgrids[1].content);
    s.subgrids[2].displayContent = cloneDeep(s.subgrids[2].content);
    s.subgrids[3].displayContent = cloneDeep(s.subgrids[3].content);

    s.subgrids[0].solvedContent = cloneDeep(rubigramData.solutionGrid[0]);
    s.subgrids[1].solvedContent = cloneDeep(rubigramData.solutionGrid[1]);
    s.subgrids[2].solvedContent = cloneDeep(rubigramData.solutionGrid[2]);
    s.subgrids[3].solvedContent = cloneDeep(rubigramData.solutionGrid[3]);

    // this is just for resetting for when user logs in or out
    s.subgrids.forEach(subgrid => {
      subgrid.flippedHorizontally = false;
      subgrid.flippedVertically = false;
      subgrid.degreesRotated = 0;
    });

    s.degreesRotated = 0;

    // printQuadrantGrid(s);
  });
};

export const onSolve = () => {
  const { time, puzzleId } = RubigramStore.getRawState();
  
  setTimeout(() => {
    RubigramStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, STATS_DIALOG_DELAY);
  
  savePuzzleProgress(PuzzleNames.Rubigram, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
  });
};


export const onReveal = () => {
  RubigramStore.update(s => {
    s.revealed = true;
    s.isPaused = false;

    savePuzzleProgress(PuzzleNames.Rubigram, {
      puzzleId: s.puzzleId,
      revealed: true,
      solved: false,
      time: s.time,
    });
  });
};


// this is called on hard pause and on navigate
export const saveRubigram = () => {
  const { solved, revealed, time, puzzleId } = RubigramStore.getRawState();

  if (solved || revealed) return;

  // just updating progress on pause or navigate away
  savePuzzleProgress(PuzzleNames.Rubigram, {
    puzzleId,
    revealed,
    solved,
    time,
  });
};

const areGridsIdentical = (subgrid: {
  content: string[][];
  solvedContent: string[][];
}) => {
  for (let i = 0; i < subgrid.content.length; i++) {
    for (let j = 0; j < subgrid.content[i].length; j++) {
      if (subgrid.content[i][j] !== subgrid.solvedContent[i][j]) {
        return false;
      }
    }
  }

  return true;
};

type CompleteGridParam = { subgrids: { content: string[][]; solvedContent: string[][] }[] };
const checkIsSolved = (grid: CompleteGridParam) => {
  let solved = true;

  for (let i=0; i<grid.subgrids.length; i++) {
    solved = areGridsIdentical(grid.subgrids[i]);
    if (!solved) break;
  }

  if (solved) onSolve();

  return solved;
};


////////////////////////////////////////////////////////////////////////


// the `isAccountingForRotation` param avoids an infinite loop when the one fn calls the other when the grid is at 90/-90 degrees.
export const flipSubgridVisualHorizontally = (
  subgridIdForCss: number,
  positionalSubgridIdForData: number,
  isAccountingForRotation?: boolean,
) => {
  const puzzleRotation = RubigramStore.getRawState().degreesRotated;
  const puzzleIsRotated90Degrees = puzzleRotation % 180 !== 0;

  if (puzzleIsRotated90Degrees && !isAccountingForRotation) {
    flipSubgridVisualVertically(subgridIdForCss, positionalSubgridIdForData, true);
    return;
  }

  RubigramStore.update(s => {
    s.activeSubgrid = subgridIdForCss;

    const subgridForCss = s.subgrids[subgridIdForCss];
    const subgridForData = s.subgrids[positionalSubgridIdForData];

    subgridForCss.flippedHorizontally = !subgridForCss.flippedHorizontally;
    subgridForCss.isInFlippingAnimation = true;

    // disable this as soon as animation ends
    setTimeout(() => {
      RubigramStore.update(_s => {
        _s.subgrids[subgridIdForCss].isInFlippingAnimation = false;
      });
    }, 300);
    
    if (isAccountingForRotation) {
      subgridForData.content = flipSubgridDataVertically(subgridForData.content);
    } else {
      subgridForData.content = flipSubgridDataHorizontally(subgridForData.content);
    }

    s.solved = checkIsSolved(s);

    // printQuadrantGrid(s);
  });
};

export const flipSubgridVisualVertically = (
  subgridIdForCss: number,
  positionalSubgridIdForData: number,
  isAccountingForRotation?: boolean,
) => {
  const puzzleRotation = RubigramStore.getRawState().degreesRotated;
  const puzzleIsRotated90Degrees = puzzleRotation % 180 !== 0;

  if (puzzleIsRotated90Degrees && !isAccountingForRotation) {
    flipSubgridVisualHorizontally(subgridIdForCss, positionalSubgridIdForData, true);
    return;
  }


  RubigramStore.update(s => {
    s.activeSubgrid = subgridIdForCss;

    const subgridForCss = s.subgrids[subgridIdForCss];
    const subgridForData = s.subgrids[positionalSubgridIdForData];

    subgridForCss.flippedVertically = !subgridForCss.flippedVertically;
    subgridForCss.isInFlippingAnimation = true;

    // disable this as soon as animation ends
    setTimeout(() => {
      RubigramStore.update(_s => {
        _s.subgrids[subgridIdForCss].isInFlippingAnimation = false;
      });
    }, 300);

    if (isAccountingForRotation) {
      subgridForData.content = flipSubgridDataHorizontally(subgridForData.content);
    } else {
      subgridForData.content = flipSubgridDataVertically(subgridForData.content);
    }

    s.solved = checkIsSolved(s);

    // printQuadrantGrid(s);
  });
};

export const rotateSubgridVisual = (
  subgridIdForCss: number,
  positionalSubgridIdForData: number,
  clockwise: boolean,
) => {
  RubigramStore.update(s => {
    s.activeSubgrid = subgridIdForCss;

    const subgridForCss = s.subgrids[subgridIdForCss];
    const subgridForData = s.subgrids[positionalSubgridIdForData];

    if (subgridForCss.flippedHorizontally !== subgridForCss.flippedVertically) {
      subgridForCss.degreesRotated = subgridForCss.degreesRotated + (clockwise ? -90 : 90);
    } else {
      subgridForCss.degreesRotated = subgridForCss.degreesRotated + (clockwise ? 90 : -90);
    }

    if (clockwise) {
      subgridForData.content = rotateSubgridDataClockwise(subgridForData.content);
    } else {
      subgridForData.content = rotateSubgridDataCounterClockwise(subgridForData.content);
    }

    s.solved = checkIsSolved(s);

    // printQuadrantGrid(s);
  });
};

export const rotatePuzzleVisual = (clockwise: boolean) => {
  RubigramStore.update(s => {
    s.degreesRotated = s.degreesRotated + (clockwise ? 90 : -90);

    if (clockwise) {
      rotatePuzzleDataClockwise(s);
    } else {
      rotatePuzzleDataCounterClockwise(s);
    }

    s.solved = checkIsSolved(s);

    // printQuadrantGrid(s);
  });
};

// These fns below do the actual updates to the grid and subgrid data and are used
// by the visual fns above.
// The visual fns make sure the right thing is updating VISUALLY to the user,
// whereas these functions below just make the more low-level simple changes to specified
// parts of the underlying data matrices. remember, the html content never actually changes.
// Example: user wants to horizontally flip the lower right subgrid, but due to transformations
// that actually means vertically flipping the upper left subgrid. so the visual fn figures out
// which subgrid it REALLY is, then one of these fns below would just flip the upper left subgrid.

const flipSubgridDataHorizontally = (subgrid: string[][]) => {
  const flippedSubgrid = [];
  for (let row of subgrid) {
    flippedSubgrid.push(row.slice().reverse());
  }
  return flippedSubgrid;
};

const flipSubgridDataVertically = (subgrid: string[][]) => {
  const flippedSubgrid = [];
  for (let i = subgrid.length - 1; i >= 0; i--) {
    flippedSubgrid.push(subgrid[i].slice());
  }
  return flippedSubgrid;
};

const rotateSubgridDataClockwise = (subgrid: string[][]) => {
  const rotatedSubgrid: string[][] = [];
  for (let i = 0; i < subgrid.length; i++) {
    rotatedSubgrid.push([]);
    for (let j = 0; j < subgrid.length; j++) {
      rotatedSubgrid[i].push(subgrid[subgrid.length - j - 1][i]);
    }
  }
  return rotatedSubgrid;
};

const rotateSubgridDataCounterClockwise = (subgrid: string[][]) => {
  const rotatedSubgrid: string[][] = [];
  for (let i = 0; i < subgrid.length; i++) {
    rotatedSubgrid.push([]);
    for (let j = 0; j < subgrid.length; j++) {
      rotatedSubgrid[i].push(subgrid[j][subgrid.length - i - 1]);
    }
  }
  return rotatedSubgrid;
};

type GridParam = { subgrids: { content: string[][]; solvedContent?: string[][] }[] };

const rotatePuzzleDataClockwise = (grid: GridParam) => {
  // First rotate the content of the subgrids
  const rotatedTopLeft = rotateSubgridDataClockwise(grid.subgrids[0].content);
  const rotatedTopRight = rotateSubgridDataClockwise(grid.subgrids[1].content);
  const rotatedBottomRight = rotateSubgridDataClockwise(grid.subgrids[2].content);
  const rotatedBottomLeft = rotateSubgridDataClockwise(grid.subgrids[3].content);

  // Then rotate the position of the subgrids
  // Bottom left becomes top left. Top left becomes top right.
  // Top right becomes bottom right. Bottom right becomes bottom left.
  grid.subgrids[0].content = rotatedBottomLeft;
  grid.subgrids[1].content = rotatedTopLeft;
  grid.subgrids[2].content = rotatedTopRight;
  grid.subgrids[3].content = rotatedBottomRight;
};

const rotatePuzzleDataCounterClockwise = (grid: GridParam) => {
  // First rotate the content of the subgrids
  const rotatedTopLeft = rotateSubgridDataCounterClockwise(grid.subgrids[0].content);
  const rotatedTopRight = rotateSubgridDataCounterClockwise(grid.subgrids[1].content);
  const rotatedBottomRight = rotateSubgridDataCounterClockwise(grid.subgrids[2].content);
  const rotatedBottomLeft = rotateSubgridDataCounterClockwise(grid.subgrids[3].content);

  // Then rotate the position of the subgrids
  grid.subgrids[0].content = rotatedTopRight;
  grid.subgrids[1].content = rotatedBottomRight;
  grid.subgrids[2].content = rotatedBottomLeft;
  grid.subgrids[3].content = rotatedTopLeft;
};



///////////


function printQuadrantGrid(grid: GridParam): void {
  // only enable this for debugging purposes.
  return;
  // Create an empty 6x6 grid to store the combined result
  const parentGrid: string[][] = Array.from({ length: 6 }, () => Array(6).fill(' '));

  // Function to merge a subgrid into the parent grid at a specific position
  function mergeSubgrid(subgrid: string[][], rowOffset: number, colOffset: number): void {
    for (let r = 0; r < subgrid.length; r++) {
      for (let c = 0; c < subgrid[r].length; c++) {
        parentGrid[rowOffset + r][colOffset + c] = subgrid[r][c];
      }
    }
  }

  const subgridPositions = [
    [0, 0], [0, 3], [3, 3], [3, 0] // Top-left, Top-right, Bottom-right, Bottom-left
  ];

  // Process each grid
  grid.subgrids.forEach((subgrid, subgridIndex) => {
    const { content } = subgrid;
    const [rowOffset, colOffset] = subgridPositions[subgridIndex];
    mergeSubgrid(content, rowOffset, colOffset);
  });

  // Print the parent grid
  console.log('\n-------------------------\n')
  parentGrid.forEach(row => {
    console.log(row.join(''));
  });
}